/**
 * This module provides a custom hook `useAuth0SilentRefresh` to handle silent refreshes of Auth0 ID tokens.
 *
 * The hook integrates the Auth0 React SDK, Redux, and utility functions to manage the lifecycle of the ID token,
 * ensuring it remains valid or refreshed when necessary.
 *
 * Key features:
 * - Monitors the token expiration and triggers a refresh using `getAccessTokenSilently` when it is about to expire.
 * - Uses a helper function `ExtractTokenDetails` to extract token details such as expiration.
 * - Updates the Redux store with the new token, ensuring other parts of the app have access to the latest token.
 * - Includes error handling for token refresh failures and logs out users when needed, displaying relevant toast notifications.
 * - Ensures state consistency by waiting for the Redux store to be updated before resolving.
 *
 * The `silentRefresh` function:
 * - Checks if the token is about to expire, or forces a refresh if needed.
 * - Retrieves and updates the ID token and dispatches it to the Redux store.
 * - Handles authentication errors (e.g., expired sessions) by logging out users and displaying appropriate toast notifications.
 */

import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import { setAuth0IdToken } from '@/redux/reducers/authenticationSlice';
import { store } from '@/redux/store';
import { authenticationSliceName } from '@/redux/constants';
import { ExtractTokenDetails } from '@/Utils/Auth0TokenUtils.tsx';
import ShowToast from '@/Components/Toast/showToastTemplate';
import {
  AUTH0_CLIENT_ID,
  GIGASHEETUSERDATA_AUTH0_API_ID,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_INFO,
} from '@/Utils/constants';

const useAuth0SilentRefresh = () => {
  const { getIdTokenClaims, getAccessTokenSilently, logout } = useAuth0();
  const dispatch = useDispatch();

  const waitForStateUpdate = (selector, value) => {
    return new Promise((resolve) => {
      const unsubscribe = store.subscribe(() => {
        if (selector(store.getState()) === value) {
          unsubscribe();
          resolve();
        }
      });
    });
  };

  const handleLoginRequired = (error, fileAndFunctionName) => {
    const sessionExpiredErrors = [
      'Login required',
      'Get access token failed',
      'Invalid authorization code',
    ];

    if (sessionExpiredErrors.includes(error?.message)) {
      ShowToast({
        type: TOAST_TYPE_INFO,
        contentHeader: 'Login Required',
        text: 'Your session has expired. Redirecting to login page in 5 seconds...',
      });
    } else {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: 'Authentication error. Redirecting to login page in 5 seconds...',
        errorContext: error,
        fileAndFunction: fileAndFunctionName,
      });
    }

    setTimeout(async () => {
      logout({ client_id: AUTH0_CLIENT_ID });
    }, 5000);
  };

  const silentRefresh = async (force = false, fileAndFunctionName = null) => {
    try {
      let tokenWillExpireSoon = false;
      if (!force) {
        try {
          const token = await getAccessTokenSilently({
            ignoreCache: true,
            audience: GIGASHEETUSERDATA_AUTH0_API_ID,
            scope: 'openid',
          });
          const tokenDetails = ExtractTokenDetails(token);
          tokenWillExpireSoon = tokenDetails.willExpireSoon;
        } catch (e) {
          tokenWillExpireSoon = true;
        }
      }
      if (force || tokenWillExpireSoon) {
        const token = await getAccessTokenSilently({
          ignoreCache: true,
          audience: GIGASHEETUSERDATA_AUTH0_API_ID,
          scope: 'openid',
        });
        if (!token) {
          throw new Error('Failed to retrieve access token.');
        }

        // Dispatch the new token and wait for state update
        dispatch(setAuth0IdToken(token));
        await waitForStateUpdate((state) => state[authenticationSliceName].auth0IdToken, token);
      }
    } catch (error) {
      handleLoginRequired(error, fileAndFunctionName);
      throw error;
    }
  };

  return { silentRefresh };
};

export default useAuth0SilentRefresh;
